import { ViewWidgetHelper } from '../../view/ViewWidgetHelper.js';
import { ViewCmdType } from '../../view/IViewWidget.js';
import { AsyncServerCmd, ProcStatus } from './AsyncServerCmd.js';

/**
 * Универсальный опросчик batch-статусов для асинхронных процессов.
 * Использует ViewWidgetHelper.ExecCmd с:
 *   cmdType: asyncprocess, cmd: 'progressBatch',
 *   options: { items: ProcessCmdArg[] }
 *
 * Ответ сервера ожидается в виде:
 *   type: AsyncServerCmd.procBatch
 *   payload: { items: ProcStatusPayload[]; deleted: number[] }
 *
 * Поддерживает привязку произвольных данных к procID
 * (например, UppyFile для постпроцессинга).
 */
class AsyncProcessPoller {
    constructor(opts) {
        /** procID → { lastCmd, lastReport, data } */
        this.registry = new Map();
        this.timer = null;
        this.isBusy = false;
        this.handlers = {};
        this.el = opts.element;
        this.owner = opts.owner;
        this.interval = Math.max(300, opts.intervalMs ?? 1200);
        this.noReports = !!opts.noReports;
    }
    /** Установить хендлеры */
    setHandlers(h) { this.handlers = h; }
    /** Добавить процесс под наблюдение + опционально userData */
    add(procID, lastCmd = 0, lastReport = 0, data) {
        this.registry.set(procID, { lastCmd, lastReport, data });
        this.ensureStarted();
    }
    /** Обновить lastCmd */
    setLastCmd(procID, lastCmd) {
        const rec = this.registry.get(procID);
        if (rec)
            rec.lastCmd = lastCmd;
    }
    /** Обновить lastReport */
    setLastReport(procID, lastReport) {
        const rec = this.registry.get(procID);
        if (rec)
            rec.lastReport = lastReport;
    }
    /** Получить привязанный объект */
    getData(procID) {
        return this.registry.get(procID)?.data;
    }
    /** Снять процесс с наблюдения */
    remove(procID) {
        this.registry.delete(procID);
        this.stopIfIdle();
    }
    /** Очистить все процессы */
    clear() {
        this.registry.clear();
        this.stopIfIdle();
    }
    /** Полная остановка */
    destroy() {
        this.stopTimer();
        this.isBusy = false;
        this.registry.clear();
    }
    // --- внутреннее ---
    /** Запустить таймер при необходимости */
    ensureStarted() {
        if (this.timer != null)
            return;
        this.timer = window.setInterval(() => this.tick(), this.interval);
    }
    /** Снять интервальный таймер (без побочных эффектов). */
    stopTimer() {
        if (this.timer != null) {
            window.clearInterval(this.timer);
            this.timer = null;
        }
    }
    /** Остановить, если процессов не осталось */
    stopIfIdle() {
        if (this.registry.size === 0 && this.timer != null) {
            this.stopTimer();
            this.handlers.onIdle?.();
        }
    }
    /** Один тик опроса */
    tick() {
        if (this.isBusy)
            return;
        if (this.registry.size === 0) {
            this.stopIfIdle();
            return;
        }
        // 2026-06-04: элемент отсоединён от DOM — гасим таймер принудительно.
        // stopIfIdle() здесь не подходит: он чистит таймер только при пустом реестре,
        // иначе таймер тикал бы вхолостую ("зомби-таймер"). Реестр сохраняем —
        // повторный add() перезапустит наблюдение через ensureStarted().
        if (!this.el.isConnected) {
            this.stopTimer();
            return;
        }
        const items = [];
        for (const [procID, rec] of this.registry) {
            items.push({
                procID,
                lastCmd: rec.lastCmd ?? 0,
                lastReport: this.noReports ? -1 : (rec.lastReport ?? 0),
            });
        }
        if (items.length === 0) {
            this.stopIfIdle();
            return;
        }
        this.isBusy = true;
        // command-contract: Nordbox.Web.Core/wwwroot/src_tests/contracts/async-process-progress-batch.md — при изменении payload/route/DTO обновить contract, fixtures и TS/C# tests
        ViewWidgetHelper.ExecCmd(this.el, {
            cmdType: ViewCmdType.asyncprocess,
            cmd: "progressBatch",
            options: { items },
            onSuccess: (data) => {
                try {
                    // Если тип не batch — адресный callback по procID (если он есть)
                    if (data.type !== AsyncServerCmd.procBatch) {
                        // подстрахуемся: procID может лежать либо на корне, либо внутри payload
                        const raw = data.procID ?? data.payload?.procID;
                        const pid = Number(raw) | 0;
                        if (pid > 0) {
                            const rec = this.registry.get(pid);
                            if (rec) {
                                this.handlers.onCallback?.(pid, data, rec.data);
                                // Обработано
                                return;
                            }
                        }
                        // Не обработано
                        return;
                    }
                    // 1) Удалённые процессы
                    const deleted = data.deleted ?? [];
                    for (const pid of deleted) {
                        const rec = this.registry.get(pid);
                        if (!rec)
                            continue;
                        this.handlers.onDeleted?.(pid, rec.data);
                        this.registry.delete(pid);
                    }
                    // 2) Активные процессы
                    const list = data.items ?? [];
                    for (const item of list) {
                        //console.log('On success item', item);
                        const pid = (item.id ?? 0) | 0;
                        const rec = this.registry.get(pid);
                        if (!pid || !rec)
                            continue;
                        if (typeof item.lastCmd === "number")
                            rec.lastCmd = item.lastCmd;
                        if (typeof item.lastReport === "number")
                            rec.lastReport = item.lastReport;
                        //console.log('Check status', item.status);
                        switch (item.status) {
                            case ProcStatus.Waiting:
                            case ProcStatus.Working:
                                this.handlers.onWorking?.(pid, item, rec.data);
                                break;
                            case ProcStatus.Done:
                                this.handlers.onDone?.(pid, item, rec.data);
                                this.registry.delete(pid);
                                break;
                            case ProcStatus.Error:
                            case ProcStatus.Aborted:
                                this.handlers.onFailed?.(pid, item, rec.data);
                                this.registry.delete(pid);
                                break;
                            default:
                                console.error('unknown status', item.status);
                                break;
                        }
                    }
                    this.stopIfIdle();
                }
                finally {
                    this.isBusy = false;
                }
            },
            onError: () => { this.isBusy = false; },
            setBusy: false
        }, this.owner);
    }
}

export { AsyncProcessPoller };
